import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import type { W_WorkOrder_FindAllRevisionsForWorkOrder_VM } from 'ab-api/web/workOrder/findAllRevisionsForWorkOrder';

import * as TimeUtils from '@acceligentllc/shared/utils/time';
import * as UserUtils from '@acceligentllc/shared/utils/user';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { WorkOrderStatus } from '@acceligentllc/shared/enums/workOrderStatus';

import { CURRENT_WORK_ORDER_REVISION_ITEM } from 'ab-common/constants/value';

import Dropdown from 'af-components/Controls/Dropdown';

import * as WorkOrderActions from 'af-actions/workOrder';

import type { RevisionDropdownWorkOrder } from './types';

interface OwnProps {
	selectRevision: (revisionId: number, isCurrentWorkOrder: boolean) => void;
	workOrder: RevisionDropdownWorkOrder;
}

type RevisionWithSelector = W_WorkOrder_FindAllRevisionsForWorkOrder_VM & {
	selector: string;
};

type Props = OwnProps & ConnectedProps<typeof connector>;

const _getDefaultRevision = (workOrder: RevisionDropdownWorkOrder) => {
	return {
		id: workOrder?.id,
		dueDate: workOrder?.dueDate,
		code: workOrder?.code,
		revision: workOrder?.revision,
		updatedAt: workOrder?.updatedAt,
		status: workOrder?.status,
		selector: `${workOrder?.id}${workOrder?.revision}`,
		additionalText: CURRENT_WORK_ORDER_REVISION_ITEM,
		isCurrentWorkOrder: true,
		updatedByFullName: UserUtils.getUserName(
			workOrder.updatedBy?.firstName && workOrder.updatedBy?.lastName
				? { firstName: workOrder.updatedBy.firstName, lastName: workOrder.updatedBy.lastName }
				: null
		),
	};
};

const _getStatusIcon = (status: Nullable<WorkOrderStatus>) => {
	switch (status) {
		case WorkOrderStatus.PUBLISHED:
		case WorkOrderStatus.LOCKED:
			return <span className="icon-check text-green" />;
		case WorkOrderStatus.CANCELED:
			return <span className="icon-warning text-red" />;
		case WorkOrderStatus.OUTDATED:
			return <span className="icon-clock text-orange" />;
		default:
			return null;
	}
};

const currentWorkOrderAdditionalDisplayText = (option: W_WorkOrder_FindAllRevisionsForWorkOrder_VM) => {
	const { status, additionalText, updatedByFullName } = option;
	return status === WorkOrderStatus.OUTDATED
		? ` ${additionalText} by ${updatedByFullName}`
		: ` ${updatedByFullName ?? ''}`;
};

const _renderRevisionItem = (option: RevisionWithSelector) => {
	if (!option) {
		return (<div className="clear-option" />);
	}

	const { revision, updatedAt, status, additionalText, disabled, isCurrentWorkOrder } = option;

	const revisionDisplayText = isCurrentWorkOrder
		? `Current - ${updatedAt && TimeUtils.formatDate(updatedAt, TimeFormat.FULL_DATE_SLASHES, TimeFormat.ISO_DATETIME)}`
		: `Rev. ${revision} - ${updatedAt && TimeUtils.formatDate(updatedAt, TimeFormat.FULL_DATE_SLASHES, TimeFormat.ISO_DATETIME)}`;
	const additionalDisplayText = isCurrentWorkOrder
		? currentWorkOrderAdditionalDisplayText(option)
		: ` ${additionalText}`;

	return (
		<span className="work-order-info-modal__revision-item">
			{!disabled && _getStatusIcon(status)}
			<strong>{!disabled && revisionDisplayText}</strong>
			<span className="work-order-info-modal__revision-item-details">{additionalDisplayText}</span>
		</span>
	);
};

const RevisionDropdown: React.FC<Props> = (props: Props) => {
	const { workOrder, lazyLoadRevisions, selectRevision } = props;

	const [workOrderRevisions, setWorkOrderRevisions] = React.useState<RevisionWithSelector[]>([]);
	const [defaultWorkOrderRevision, setDefaultWorkOrderRevision] = React.useState<RevisionWithSelector>(
		_getDefaultRevision(workOrder)
	);

	React.useEffect(() => {
		setDefaultWorkOrderRevision(_getDefaultRevision(workOrder));
	}, [workOrder]);

	const loadRevisions = React.useCallback(async (isLazyLoaded: boolean) => {
		if (!isLazyLoaded) {
			const revisions = await lazyLoadRevisions(workOrder.workOrderId ?? workOrder.id);
			setWorkOrderRevisions(revisions.map((_rev) => ({ ..._rev, selector: `${_rev.id}${_rev.revision}` })));
		}
	}, [lazyLoadRevisions, workOrder.workOrderId, workOrder.id]);

	const selectWorkOrderRevision = React.useCallback(async (workOrderRevision: Nullable<W_WorkOrder_FindAllRevisionsForWorkOrder_VM>) => {
		if (!workOrderRevision) {
			throw new Error('Invalid revision selected');
		}

		const { id, isCurrentWorkOrder } = workOrderRevision;

		if (!id) {
			throw new Error('No revision to select');
		}
		selectRevision(id, !!isCurrentWorkOrder);
	}, [selectRevision]);

	return (
		<div className="work-order-info-modal__revisions">
			<div className="work-order-info-modal__revisions-dropdown-container">
				<Dropdown<RevisionWithSelector>
					defaultValue={defaultWorkOrderRevision}
					fullWidth={true}
					id="accqa__revision-dropdown"
					onLazyLoad={loadRevisions}
					onValueChange={selectWorkOrderRevision}
					options={workOrderRevisions}
					renderMenuItem={_renderRevisionItem}
					valueKey="selector"
					withBorder={true}
					withCaret={true}
				/>
			</div>
		</div>
	);
};

function mapDispatchToProps() {
	return {
		lazyLoadRevisions: WorkOrderActions.lazyLoadRevisions,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(RevisionDropdown);
